import type { Transition } from "@uirouter/core";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { getWebSourceRequiredChanges } from "../../handlers/webSource/webSourceFilterConsistencyHandler";
import {
    shouldApplyFilterConsistency,
    saveFilterConsistencyAfterFirstNavigation,
    testAndApplyChanges,
    EUrlKeys,
} from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { getOpenCountryFilterRequiredChanges } from "app/routes/handlers/filter-consistency/handlers/country/openCountryFilterConsistencyHandler";
import { isEmpty } from "lodash";
import { getDurationRequiredChanges } from "app/routes/handlers/filter-consistency/handlers/duration/durationFilterConsistencyHandler";

export const onRedirectDisplayPages = (configId?: string, overrideToParams?: Record<string, any>): transitionHookFn => {
    return (transition: Transition) => {
        const fromState = transition.from() as IRouterState;
        const toParams = overrideToParams ?? transition.params("to");
        if (shouldApplyFilterConsistency(fromState, toParams, EUrlKeys.KEY)) {
            const swRedux = SwRedux.getInstance();
            const targetState = transition.to() as IRouterState;
            const {
                digitalSuiteSelectedCountry,
                digitalSuiteOpenSelectedCountry,
                digitalSuiteSelectedDuration,
                digitalSuiteSelectedWebSource,
                isNewSession,
            } = swRedux.getState().usersDigitalSuiteFiltersSelection;
            if (!isNewSession) {
                const componentConfigId = configId ?? proNavigator.getConfigIdFromStateConfig(targetState, toParams);

                const durationChanges = getDurationRequiredChanges(
                    componentConfigId,
                    digitalSuiteSelectedDuration,
                    targetState,
                    toParams,
                );

                const countryChanges = getOpenCountryFilterRequiredChanges(
                    componentConfigId,
                    digitalSuiteSelectedCountry,
                    digitalSuiteOpenSelectedCountry,
                    targetState,
                    toParams,
                );

                const webSourceChanges = getWebSourceRequiredChanges(
                    durationChanges.isAllowed,
                    digitalSuiteSelectedDuration,
                    countryChanges.isAllowed,
                    countryChanges.value ?? digitalSuiteSelectedCountry,
                    digitalSuiteSelectedWebSource,
                    targetState,
                    toParams,
                );

                const changedParams = testAndApplyChanges(
                    countryChanges,
                    durationChanges,
                    webSourceChanges,
                    countryChanges.value ?? digitalSuiteSelectedCountry,
                    digitalSuiteSelectedDuration,
                    digitalSuiteSelectedWebSource,
                    toParams?.isDurationChanged,
                    toParams?.isWebSourceChanged,
                );

                if (!isEmpty(changedParams)) {
                    return { params: { ...toParams, ...changedParams } };
                }
            } else {
                // we want to save the filters after the first navigation as selected filters
                saveFilterConsistencyAfterFirstNavigation(targetState, toParams);
            }
        }
    };
};
